import { useState } from 'react'
import { Search } from 'lucide-react'
import { Button } from 'components/ds/Button'
import { Input } from 'components/ds/Input'
import { Chip } from 'components/ds/Chip'
import { RadioGroup, Radio } from 'components/ds/RadioGroup'
import type { CompanyFilterOptions } from 'components/shared/LoadsTable/CompanyFilterButton'

export const DEFAULT_COMPANY_FILTER: CompanyFilterOptions = { mode: 'show', companyNames: [], listType: 'all' }

interface CompanyFilterPanelProps {
    value: CompanyFilterOptions
    onChange: (next: CompanyFilterOptions) => void
    /** Company names to suggest in the search box (typically the unique names on the loaded board). */
    companies: string[]
    /** Panel heading — "Company" on load-hub, "Broker Blacklist" on the box-truck board. */
    title: string
    /** Close-the-popover callback for the Done button (applying is the wrapper's business, not the panel's). */
    onDone: () => void
}

/**
 * The ONE company-filter panel body (Show Only / Exclude, company-name search + chips, Preferred /
 * Blocked list radios, Clear All / Done) — shared by load-hub's CompanyFilterDropdown and the box-truck
 * Broker Blacklist popover so the UX can't drift between surfaces. Fully controlled: the wrapper owns
 * persistence and when the selection is applied; this panel only edits a CompanyFilterOptions value.
 */
export const CompanyFilterPanel = ({ value, onChange, companies, title, onDone }: CompanyFilterPanelProps) => {
    const [searchTerm, setSearchTerm] = useState('')

    const setListType = (listType: string) =>
        // Preferred/blocked filter by broker status, so clear the name selection when leaving 'all'.
        onChange({ ...value, listType: listType as CompanyFilterOptions['listType'], companyNames: listType !== 'all' ? [] : value.companyNames })

    const addCompany = (name: string) => {
        if (!value.companyNames.includes(name)) onChange({ ...value, companyNames: [...value.companyNames, name] })
        setSearchTerm('')
    }

    const filteredCompanies = searchTerm
        ? companies.filter((c) => c.toLowerCase().includes(searchTerm.toLowerCase()) && !value.companyNames.includes(c)).slice(0, 50)
        : []

    return (
        <div className="space-y-4 p-4">
            <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">{title}</h3>

            <RadioGroup
                name="company-mode"
                orientation="horizontal"
                value={value.mode}
                onChange={(mode) => onChange({ ...value, mode: mode as CompanyFilterOptions['mode'] })}
            >
                <Radio value="show" label="Show Only" />
                <Radio value="exclude" label="Exclude" />
            </RadioGroup>

            <Input
                label="Company Name"
                icon={<Search className="h-4 w-4" />}
                placeholder="Search company"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
            />

            {value.companyNames.length > 0 && (
                <div className="flex flex-wrap gap-1.5 rounded-lg bg-slate-50 p-2 dark:bg-slate-800">
                    {value.companyNames.map((name) => (
                        <Chip
                            key={name}
                            size="sm"
                            variant="soft"
                            color="primary"
                            onRemove={() => onChange({ ...value, companyNames: value.companyNames.filter((c) => c !== name) })}
                        >
                            {name}
                        </Chip>
                    ))}
                </div>
            )}

            {filteredCompanies.length > 0 && (
                <div className="max-h-48 overflow-y-auto rounded-lg border border-slate-200 dark:border-slate-700">
                    {filteredCompanies.map((name) => (
                        <button
                            key={name}
                            type="button"
                            onClick={() => addCompany(name)}
                            className="block w-full truncate px-3 py-2 text-left text-sm text-slate-700 transition-colors hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-800"
                        >
                            {name}
                        </button>
                    ))}
                </div>
            )}

            <div className="space-y-2">
                <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Preferred and Blocked List</h4>
                <RadioGroup name="company-list" orientation="vertical" value={value.listType} onChange={setListType}>
                    <Radio value="all" label="Show All Non-blocked Companies" />
                    <Radio value="preferred" label="Show Only Preferred Companies" />
                    <Radio value="blocked" label="Show Only Blocked Companies" />
                </RadioGroup>
            </div>

            <div className="flex items-center justify-between pt-1">
                <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                        onChange(DEFAULT_COMPANY_FILTER)
                        setSearchTerm('')
                    }}
                >
                    Clear All
                </Button>
                <Button variant="primary" size="sm" onClick={onDone}>
                    Done
                </Button>
            </div>
        </div>
    )
}
